/**
 * useSocket Hook
 * Manages Socket.io connection for real-time WhatsApp communication
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

// Socket.io needs to connect directly to the backend server (not through /api proxy)
const getSocketUrl = () => {
  // If explicitly set via env var, use it
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }

  // Check current hostname to determine routing
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Localhost - connect to local backend
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:4000';
    }
  }

  // Production - use Railway backend directly for Socket.io
  return 'https://revstar-main-platform-production.up.railway.app';
};

const SOCKET_URL = getSocketUrl();

export function useSocket() {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const listenersRef = useRef(new Map());

  // Get auth token
  const getToken = useCallback(() => {
    return localStorage.getItem('token') || sessionStorage.getItem('token');
  }, []);

  // Connect to socket
  const connect = useCallback(() => {
    const token = getToken();

    if (!token) {
      setConnectionError('No authentication token');
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    // Disconnect existing socket if any
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    console.log('[Socket] Connecting to WhatsApp namespace...');

    socketRef.current = io(`${SOCKET_URL}/whatsapp`, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    socketRef.current.on('connect', () => {
      console.log('[Socket] Connected to WhatsApp namespace');
      setIsConnected(true);
      setConnectionError(null);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('[Socket] Disconnected:', reason);
      setIsConnected(false);
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('[Socket] Connection error:', error.message);
      setConnectionError(error.message);
      setIsConnected(false);
    });

    socketRef.current.on('pong', () => {
      // Keep-alive response
    });
  }, [getToken]);

  // Disconnect from socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Subscribe to an event
  const on = useCallback((event, callback) => {
    if (!socketRef.current) return;

    // Store listener for cleanup
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, []);
    }
    listenersRef.current.get(event).push(callback);

    socketRef.current.on(event, callback);

    // Return unsubscribe function
    return () => {
      if (socketRef.current) {
        socketRef.current.off(event, callback);
      }
      const listeners = listenersRef.current.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }, []);

  // Emit an event
  const emit = useCallback((event, data) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  }, []);

  // Send ping to keep connection alive
  const ping = useCallback(() => {
    emit('ping');
  }, [emit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  // Auto-connect when token is available
  useEffect(() => {
    const token = getToken();
    if (token && !isConnected && !socketRef.current) {
      connect();
    }
  }, [getToken, isConnected, connect]);

  // Ping interval for keep-alive
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(ping, 25000);
    return () => clearInterval(interval);
  }, [isConnected, ping]);

  return {
    socket: socketRef.current,
    isConnected,
    connectionError,
    connect,
    disconnect,
    on,
    emit
  };
}

export default useSocket;
